import React from "react";
import { Link } from "react-router-dom";

import { xboxOnboardSiteLocationPrefix } from "../../constants";
import { FeatureFlags, useFeatureFlag } from "../../featureFlags";
import { useStrings } from "../../localization/localizationHelpers";
import { PartnerSectionNav } from "../PartnerSectionNav";
import { SupportHelpButton } from "../SupportHelpButton";
import * as styles from "./PartnerPageLayout.module.css";

type BreadcrumbStep = {
  label: string;
  to: string;
};

type PartnerPageLayoutProps = Readonly<{
  breadcrumbLabel?: string;
  breadcrumbSteps?: BreadcrumbStep[];
  children: React.ReactNode;
  showSectionNavigation?: boolean;
  wide?: boolean;
}>;

export const PartnerPageLayout: React.FC<PartnerPageLayoutProps> = ({
  breadcrumbLabel,
  breadcrumbSteps,
  children,
  showSectionNavigation = true,
  wide = false,
}) => {
  const [dashboardLabel, breadcrumbNavigationLabel] = useStrings(
    "dashboardLabel",
    "breadcrumbNavigationLabel"
  );
  const isCasesEnabled = useFeatureFlag(FeatureFlags.EnableHubSupportCases);
  const isAssistantEnabled = useFeatureFlag(FeatureFlags.EnableHubSupportAssistant);
  const isSupportEnabled = isCasesEnabled || isAssistantEnabled;
  return (
    <div className={styles.layout}>
      {isSupportEnabled && <SupportHelpButton />}
      <div className={wide ? `${styles.inner} ${styles.wide}` : styles.inner}>
        {breadcrumbLabel && (
          <nav className={styles.breadcrumb} aria-label={breadcrumbNavigationLabel}>
            <Link
              to={`${xboxOnboardSiteLocationPrefix}/dashboard`}
              className={styles.breadcrumbLink}
            >
              {dashboardLabel}
            </Link>
            {breadcrumbSteps?.map(step => (
              <React.Fragment key={step.to}>
                <span className={styles.breadcrumbSeparator} aria-hidden="true">
                  &rsaquo;
                </span>
                <Link to={step.to} className={styles.breadcrumbLink}>
                  {step.label}
                </Link>
              </React.Fragment>
            ))}
            <span className={styles.breadcrumbSeparator} aria-hidden="true">
              &rsaquo;
            </span>
            <span className={styles.breadcrumbCurrent} aria-current="page">
              {breadcrumbLabel}
            </span>
          </nav>
        )}
        {showSectionNavigation && <PartnerSectionNav />}
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
};
